import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Activity, Building2, Car, Briefcase, ShieldCheck, LineChart, Stethoscope, Flame, Ship, Wrench, Cpu, ArrowRight, FileWarning, HeartPulse, Plane, Users, BadgePercent, Sprout, GraduationCap, Target } from "lucide-react";
import LeadForm from "@/components/LeadForm";

const groups = [
  {
    title: "For Individuals & Families",
    color: "from-blue-900 to-blue-700",
    items: [
      { Icon: Stethoscope, title: "Health Insurance", body: "Family floaters, top-ups, senior citizen plans.", to: "/services/health" },
      { Icon: ShieldCheck, title: "Term Insurance", body: "Pure protection, large cover, tiny premium.", to: "/services/term" },
      { Icon: Activity, title: "Life Insurance", body: "Endowment, whole-life, guaranteed plans.", to: "/services/life" },
      { Icon: Car, title: "Motor Insurance", body: "Cars, bikes, commercial vehicles.", to: "/services/motor" },
      { Icon: ShieldCheck, title: "Personal Accident", body: "Affordable accidental death & disability cover.", to: "/services/personal-accident" },
      { Icon: HeartPulse, title: "Critical Illness", body: "Lump-sum payout on diagnosis of major illness.", to: "/services/critical-illness" },
      { Icon: Plane, title: "Travel Insurance", body: "Domestic & international trips, students abroad.", to: "/services/travel" },
      { Icon: Users, title: "Senior Citizen Health", body: "Specialised plans for parents above 60.", to: "/services/senior-citizen-health" },
      { Icon: Users, title: "Family Floater", body: "Single policy, shared cover for the whole family.", to: "/services/family-floater" },
    ],
  },
  {
    title: "Investments & Planning",
    color: "from-emerald-700 to-emerald-500",
    items: [
      { Icon: LineChart, title: "Mutual Funds & SIP", body: "Goal-based, reviewed annually.", to: "/services/mutual-funds" },
      { Icon: LineChart, title: "Step-Up SIP", body: "10% annual SIP increases — nearly double the corpus.", to: "/services/sip-step-up" },
      { Icon: Sprout, title: "Retirement Planning", body: "NPS, equity SIPs, annuity blends.", to: "/services/retirement-planning" },
      { Icon: GraduationCap, title: "Child Education Planning", body: "Long-term goal-linked SIPs.", to: "/services/child-education" },
      { Icon: BadgePercent, title: "Tax Saving Investments", body: "ELSS, 80C, 80CCD(1B) optimisation.", to: "/services/tax-saving" },
      { Icon: Target, title: "Goal-Based Investing", body: "Vacation, wedding, second home, business.", to: "/services/goal-based-investing" },
    ],
  },
  {
    title: "For Businesses & SMEs",
    color: "from-slate-900 to-blue-900",
    items: [
      { Icon: Building2, title: "Group Health Insurance", body: "Cover your team and their dependents.", to: "/services/group-health" },
      { Icon: Briefcase, title: "Group Term Life (GTL)", body: "Subsidised life cover for your team.", to: "/services/group-term-life" },
      { Icon: Flame, title: "Fire & Property", body: "Standard fire & special perils for premises.", to: "/services/fire-property" },
      { Icon: Ship, title: "Marine Insurance", body: "Cargo, transit and supply-chain covers.", to: "/services/marine" },
      { Icon: Wrench, title: "Engineering / CAR", body: "Contractors All Risk & erection covers.", to: "/services/engineering" },
      { Icon: ShieldCheck, title: "Workmen Compensation", body: "Statutory cover for blue-collar workforce.", to: "/services/workmen-comp" },
      { Icon: FileWarning, title: "Professional Indemnity", body: "Doctors, CAs, consultants, agencies.", to: "/services/professional-indemnity" },
      { Icon: Building2, title: "Directors & Officers Liability", body: "Protect board & key managerial personnel.", to: "/services/directors-liability" },
      { Icon: ShieldCheck, title: "Public Liability", body: "Third-party injury or property damage cover.", to: "/services/public-liability" },
      { Icon: Cpu, title: "Cyber Insurance", body: "Data breach, ransomware, business interruption.", to: "/services/cyber" },
      { Icon: ShieldCheck, title: "Office Package Policy", body: "All-in-one shop / office cover — fire, burglary, electronics & money.", to: "/contact" },
      { Icon: Building2, title: "Shopkeepers & SME Bundle", body: "Tailored bundle for retail, clinics, agencies & studios.", to: "/contact" },
    ],
  },
];

export default function Services() {
  return (
    <div className="bg-white pt-24 sm:pt-28" data-testid="page-services">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="max-w-3xl">
          <Badge variant="outline" className="border-blue-200 text-blue-900 bg-blue-50/60">Our solutions</Badge>
          <h1 className="font-display mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
            One advisor. <span className="text-gradient-blue">Every solution.</span>
          </h1>
          <p className="mt-5 text-slate-600 leading-relaxed text-lg">
            Personal, family, retirement, investment and business — Samota Insurance covers the
            full spectrum of financial protection and wealth-building under one roof.
          </p>
        </div>

        {groups.map((g) => (
          <div key={g.title} className="mt-16">
            <div className="flex items-center gap-3">
              <span className={`inline-block h-2 w-2 rounded-full bg-gradient-to-br ${g.color}`} />
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{g.title}</h2>
            </div>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {g.items.map((it) => (
                <Link
                  key={it.title}
                  to={it.to}
                  data-testid={`service-item-${it.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group rounded-2xl border border-slate-100 bg-white p-6 hover:-translate-y-1 hover:border-blue-200 transition soft-shadow"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-900 group-hover:bg-blue-900 group-hover:text-white transition">
                    <it.Icon className="h-5 w-5" />
                  </span>
                  <h3 className="font-display mt-4 text-base font-semibold text-slate-900">{it.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{it.body}</p>
                  <span className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-blue-900 group-hover:text-emerald-600">
                    Know more <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="bg-slate-50/60 py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <h2 className="font-display text-3xl font-semibold tracking-tight text-slate-900">
              Not sure where to start?
            </h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Take the 3-minute Needs Assessment and we'll tell you exactly which covers and
              investments your family needs — no obligation.
            </p>
            <div className="mt-6 flex gap-3">
              <Link to="/assessment"><Button className="bg-blue-900 hover:bg-blue-950 text-white">Start free assessment</Button></Link>
              <Link to="/contact"><Button variant="outline" className="border-slate-300">Talk to an advisor</Button></Link>
            </div>
          </div>
          <div className="lg:col-span-2">
            <LeadForm title="Get a quick quote" subtitle="A senior advisor will call you within 2 hours." source="services_page" testIdPrefix="services" />
          </div>
        </div>
      </section>
    </div>
  );
}
